import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Calendar,
  AlertTriangle,
  X,
  Upload,
  Download,
  FileSearch,
  RefreshCw,
  Eye,
  File,
  Image,
  CheckCircle,
  Clock,
  Users
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import LeaseStatusDashboard from "./LeaseStatusDashboard";
import LatePayers from "./LatePayers";

const LeaseHub = () => {
  const { token, isManager } = useAuth();
  const [activeTab, setActiveTab] = useState("leases");
  
  // Existing Leases State
  const [leases, setLeases] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [deleteLease, setDeleteLease] = useState(null);
  const [saving, setSaving] = useState(false);

  // Document State
  const [documents, setDocuments] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState("lease");
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  // Lease Generator State
  const [generatorDialogOpen, setGeneratorDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedLease, setGeneratedLease] = useState(null);
  const [generatorForm, setGeneratorForm] = useState({
    property_id: "",
    tenant_id: "",
    landlord_name: "",
    landlord_address: "",
    lease_start_date: "",
    lease_end_date: "",
    monthly_rent: "",
    security_deposit: "",
    late_fee_amount: "50",
    late_fee_grace_days: "5",
    pets_allowed: false,
    pet_deposit: "0",
    pet_monthly_fee: "0",
    pet_restrictions: "",
    utilities_included: [],
    parking_included: false,
    parking_fee: "0",
    early_termination_fee: "0",
    early_termination_notice_days: "60",
    additional_clauses: "",
    output_format: "both"
  });

  // Lease Parser State  
  const [parseFile, setParseFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);

  const [formData, setFormData] = useState({
    tenant_id: "",
    property_id: "",
    unit_number: "",
    start_date: "",
    end_date: "",
    monthly_rent: "",
    security_deposit: "",
    late_fee_amount: "50",
    late_fee_grace_days: "7",
    other_monthly_charges: [],
    lease_clauses: "",
    status: "active"
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [leasesRes, tenantsRes, propertiesRes, docsRes] = await Promise.all([
        axios.get(`${API}/leases`, { headers }),
        axios.get(`${API}/tenants`, { headers }),
        axios.get(`${API}/properties`, { headers }),
        axios.get(`${API}/documents`, { headers }).catch(() => ({ data: [] }))
      ]);
      setLeases(leasesRes.data);
      setTenants(tenantsRes.data);
      setProperties(propertiesRes.data);
      setDocuments(docsRes.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Document Upload
  const handleUploadDocument = async () => {
    if (!uploadFile) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("document_type", uploadType);
    if (uploadNotes) formData.append("notes", uploadNotes);

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      toast.success("Document uploaded!");
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadNotes("");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  // Lease Generation
  const handleGenerateLease = async () => {
    if (!generatorForm.property_id || !generatorForm.tenant_id || !generatorForm.lease_start_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        ...generatorForm,
        monthly_rent: parseFloat(generatorForm.monthly_rent) || 0,
        security_deposit: parseFloat(generatorForm.security_deposit) || 0,
        late_fee_amount: parseFloat(generatorForm.late_fee_amount) || 50,
        late_fee_grace_days: parseInt(generatorForm.late_fee_grace_days) || 5,
        pet_deposit: parseFloat(generatorForm.pet_deposit) || 0,
        pet_monthly_fee: parseFloat(generatorForm.pet_monthly_fee) || 0,
        parking_fee: parseFloat(generatorForm.parking_fee) || 0,
        early_termination_fee: parseFloat(generatorForm.early_termination_fee) || 0,
        early_termination_notice_days: parseInt(generatorForm.early_termination_notice_days) || 60
      };

      const response = await axios.post(`${API}/leases/generate`, payload, { headers });
      setGeneratedLease(response.data);
      toast.success("Lease generated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate lease");
    } finally {
      setGenerating(false);
    }
  };

  // Download generated lease
  const downloadLease = (base64Data, format) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { 
      type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lease_agreement.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Parse Lease
  const handleParseLease = async () => {
    if (!parseFile) {
      toast.error("Please select a file");
      return;
    }

    setParsing(true);
    const formData = new FormData();
    formData.append("file", parseFile);

    try {
      const response = await axios.post(`${API}/leases/parse-document`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      setParseResult(response.data);
      toast.success("Lease parsed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse lease");
    } finally {
      setParsing(false);
    }
  };

  // Existing lease CRUD functions
  const resetForm = () => {
    setFormData({
      tenant_id: "",
      property_id: "",
      unit_number: "",
      start_date: "",
      end_date: "",
      monthly_rent: "",
      security_deposit: "",
      late_fee_amount: "50",
      late_fee_grace_days: "7",
      other_monthly_charges: [],
      lease_clauses: "",
      status: "active"
    });
    setEditingLease(null);
  };

  const handleOpenDialog = (lease = null) => {
    if (lease) {
      setEditingLease(lease);
      setFormData({
        tenant_id: lease.tenant_id,
        property_id: lease.property_id,
        unit_number: lease.unit_number || "",
        start_date: lease.start_date,
        end_date: lease.end_date,
        monthly_rent: lease.rent_amount?.toString() || "",
        security_deposit: lease.security_deposit?.toString() || "",
        late_fee_amount: lease.late_fee_amount?.toString() || "50",
        late_fee_grace_days: lease.late_fee_grace_days?.toString() || "7",
        other_monthly_charges: lease.other_monthly_charges || [],
        lease_clauses: lease.lease_clauses || "",
        status: lease.status || "active"
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tenant_id || !formData.property_id || !formData.start_date || !formData.end_date || !formData.monthly_rent) {
      toast.error("Please fill in all required fields");
      return;
    }

    const submitData = {
      tenant_id: formData.tenant_id,
      property_id: formData.property_id,
      unit_number: formData.unit_number || null,
      start_date: formData.start_date,
      end_date: formData.end_date,
      rent_amount: parseFloat(formData.monthly_rent),
      security_deposit: parseFloat(formData.security_deposit) || parseFloat(formData.monthly_rent),
      late_fee_amount: parseFloat(formData.late_fee_amount) || 50,
      late_fee_grace_days: parseInt(formData.late_fee_grace_days) || 7,
      other_monthly_charges: formData.other_monthly_charges,
      lease_clauses: formData.lease_clauses,
      status: formData.status
    };

    setSaving(true);
    try {
      if (editingLease) {
        await axios.put(`${API}/leases/${editingLease.id}`, submitData, { headers });
        toast.success("Lease updated successfully");
      } else {
        await axios.post(`${API}/leases`, submitData, { headers });
        toast.success("Lease created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save lease");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/leases/${deleteLease.id}`, { headers });
      toast.success("Lease deleted");
      setDeleteLease(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete lease");
    }
  };

  const getLeaseStatus = (lease) => {
    const now = new Date();
    const endDate = new Date(lease.end_date);
    const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return { label: "Expired", class: "bg-red-100 text-red-700" };
    if (daysUntilExpiry < 30) return { label: `Expires in ${daysUntilExpiry}d`, class: "bg-amber-100 text-amber-700" };
    return { label: "Active", class: "bg-green-100 text-green-700" };
  };

  const filteredLeases = leases.filter(lease => {
    const tenant = tenants.find(t => t.id === lease.tenant_id);
    const property = properties.find(p => p.id === lease.property_id);
    return (
      tenant?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property?.address?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getDocIcon = (type) => {
    if (type === "tenant_id") return <Image className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-700" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="lease-hub-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Lease Management Hub</h1>
          <p className="text-slate-500 mt-1">
            Manage existing leases, generate new ones, and store documents
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1 flex-wrap">
          <TabsTrigger value="leases" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" />
            Active Leases
          </TabsTrigger>
          <TabsTrigger value="status" className="data-[state=active]:bg-white">
            <Clock className="w-4 h-4 mr-2" />
            Status & Renewals
          </TabsTrigger>
          <TabsTrigger value="late-payers" className="data-[state=active]:bg-white">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Late Payers
          </TabsTrigger>
          <TabsTrigger value="generate" className="data-[state=active]:bg-white">
            <Plus className="w-4 h-4 mr-2" />
            Generate Lease
          </TabsTrigger>
          <TabsTrigger value="parse" className="data-[state=active]:bg-white">
            <FileSearch className="w-4 h-4 mr-2" />
            Parse Lease
          </TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-white">
            <Upload className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* Lease Status & Renewals Tab */}
        <TabsContent value="status">
          <LeaseStatusDashboard />
        </TabsContent>

        {/* Late Payers Tab */}
        <TabsContent value="late-payers">
          <LatePayers />
        </TabsContent>

        {/* Active Leases Tab */}
        <TabsContent value="leases">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search leases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {isManager && (
              <Button onClick={() => handleOpenDialog()} className="bg-green-700 hover:bg-green-800">
                <Plus className="w-4 h-4 mr-2" />
                Add Lease
              </Button>
            )}
          </div>

          {filteredLeases.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No leases found</p>
            </div>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Term</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Status</TableHead>
                    {isManager && <TableHead className="w-24">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeases.map((lease) => {
                    const tenant = tenants.find(t => t.id === lease.tenant_id);
                    const property = properties.find(p => p.id === lease.property_id);
                    const status = getLeaseStatus(lease);
                    
                    return (
                      <TableRow key={lease.id}>
                        <TableCell className="font-medium">{tenant?.name || "N/A"}</TableCell>
                        <TableCell>{property?.address || "N/A"}</TableCell>
                        <TableCell className="text-sm">
                          {lease.start_date} to {lease.end_date}
                        </TableCell>
                        <TableCell>${lease.rent_amount?.toLocaleString()}/mo</TableCell>
                        <TableCell>
                          <Badge className={status.class}>{status.label}</Badge>
                        </TableCell>
                        {isManager && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => handleOpenDialog(lease)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleteLease(lease)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* Generate New Lease Tab */}
        <TabsContent value="generate">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Illinois Residential Lease Generator</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Property *</Label>
                    <Select
                      value={generatorForm.property_id}
                      onValueChange={(v) => setGeneratorForm({...generatorForm, property_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tenant *</Label>
                    <Select
                      value={generatorForm.tenant_id}
                      onValueChange={(v) => setGeneratorForm({...generatorForm, tenant_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select tenant" />
                      </SelectTrigger>
                      <SelectContent>
                        {tenants.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Landlord Name *</Label>
                    <Input
                      value={generatorForm.landlord_name}
                      onChange={(e) => setGeneratorForm({...generatorForm, landlord_name: e.target.value})}
                      placeholder="Property owner/LLC name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Landlord Address</Label>
                    <Input
                      value={generatorForm.landlord_address}
                      onChange={(e) => setGeneratorForm({...generatorForm, landlord_address: e.target.value})}
                      placeholder="Owner mailing address"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Lease Start Date *</Label>
                    <Input
                      type="date"
                      value={generatorForm.lease_start_date}
                      onChange={(e) => setGeneratorForm({...generatorForm, lease_start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Lease End Date *</Label>
                    <Input
                      type="date"
                      value={generatorForm.lease_end_date}
                      onChange={(e) => setGeneratorForm({...generatorForm, lease_end_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monthly Rent *</Label>
                    <Input
                      type="number"
                      value={generatorForm.monthly_rent}
                      onChange={(e) => setGeneratorForm({...generatorForm, monthly_rent: e.target.value})}
                      placeholder="1500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Security Deposit</Label>
                    <Input
                      type="number"
                      value={generatorForm.security_deposit}
                      onChange={(e) => setGeneratorForm({...generatorForm, security_deposit: e.target.value})}
                      placeholder="Same as rent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Late Fee Amount</Label>
                    <Input
                      type="number"
                      value={generatorForm.late_fee_amount}
                      onChange={(e) => setGeneratorForm({...generatorForm, late_fee_amount: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Grace Period (days)</Label>
                    <Input
                      type="number"
                      value={generatorForm.late_fee_grace_days}
                      onChange={(e) => setGeneratorForm({...generatorForm, late_fee_grace_days: e.target.value})}
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">Pet Policy</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Checkbox
                      checked={generatorForm.pets_allowed}
                      onCheckedChange={(v) => setGeneratorForm({...generatorForm, pets_allowed: v})}
                    />
                    <span className="text-sm">Pets Allowed</span>
                  </div>
                  {generatorForm.pets_allowed && (
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <Input
                        type="number"
                        placeholder="Pet Deposit"
                        value={generatorForm.pet_deposit}
                        onChange={(e) => setGeneratorForm({...generatorForm, pet_deposit: e.target.value})}
                      />
                      <Input
                        type="number"
                        placeholder="Monthly Pet Fee"
                        value={generatorForm.pet_monthly_fee}
                        onChange={(e) => setGeneratorForm({...generatorForm, pet_monthly_fee: e.target.value})}
                      />
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">Parking</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Checkbox
                      checked={generatorForm.parking_included}
                      onCheckedChange={(v) => setGeneratorForm({...generatorForm, parking_included: v})}
                    />
                    <span className="text-sm">Parking Included</span>
                  </div>
                  {generatorForm.parking_included && (
                    <Input
                      type="number"
                      placeholder="Monthly Parking Fee (0 if free)"
                      value={generatorForm.parking_fee}
                      onChange={(e) => setGeneratorForm({...generatorForm, parking_fee: e.target.value})}
                      className="mt-3"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Additional Clauses</Label>
                  <Textarea
                    value={generatorForm.additional_clauses}
                    onChange={(e) => setGeneratorForm({...generatorForm, additional_clauses: e.target.value})}
                    placeholder="Any additional terms or conditions..."
                    rows={3}
                  />
                </div>

                <Button
                  onClick={handleGenerateLease}
                  disabled={generating}
                  className="w-full bg-green-700 hover:bg-green-800"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Lease
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Generated Lease Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Generated Lease</CardTitle>
              </CardHeader>
              <CardContent>
                {!generatedLease ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Fill out the form and click "Generate Lease"</p>
                    <p className="text-sm mt-2">Your lease will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-700">Lease Generated Successfully!</span>
                    </div>

                    <div className="flex gap-3">
                      {generatedLease.pdf_base64 && (
                        <Button
                          onClick={() => downloadLease(generatedLease.pdf_base64, 'pdf')}
                          className="flex-1"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      )}
                      {generatedLease.docx_base64 && (
                        <Button
                          variant="outline"
                          onClick={() => downloadLease(generatedLease.docx_base64, 'docx')}
                          className="flex-1"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Word
                        </Button>
                      )}
                    </div>

                    <div className="border rounded-lg p-4 bg-slate-50 max-h-96 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap font-mono">
                        {generatedLease.preview_text}
                      </pre>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Parse Existing Lease Tab */}
        <TabsContent value="parse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Upload Lease to Parse</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    parseFile ? "border-green-300 bg-green-50" : "border-slate-300 hover:border-green-400"
                  }`}
                >
                  {parseFile ? (
                    <div className="space-y-3">
                      <FileText className="w-12 h-12 text-green-600 mx-auto" />
                      <p className="font-medium">{parseFile.name}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setParseFile(null)}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-12 h-12 text-slate-400 mx-auto" />
                      <p className="font-medium">Drop lease document here</p>
                      <p className="text-sm text-slate-500">PDF, TXT, PNG, JPEG</p>
                    </div>
                  )}
                  <input
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={(e) => setParseFile(e.target.files?.[0] || null)}
                    accept=".pdf,.txt,.png,.jpg,.jpeg"
                    style={{ position: 'relative' }}
                  />
                </div>

                <Button
                  onClick={handleParseLease}
                  disabled={!parseFile || parsing}
                  className="w-full bg-green-700 hover:bg-green-800"
                >
                  {parsing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing with AI...
                    </>
                  ) : (
                    <>
                      <FileSearch className="w-4 h-4 mr-2" />
                      Parse Lease Document
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Extracted Information</CardTitle>
              </CardHeader>
              <CardContent>
                {!parseResult ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileSearch className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Upload a lease to extract information</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {parseResult.rent_amount && (
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Monthly Rent</span>
                        <span className="font-bold">${parseResult.rent_amount}</span>
                      </div>
                    )}
                    {parseResult.security_deposit && (
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Security Deposit</span>
                        <span className="font-bold">${parseResult.security_deposit}</span>
                      </div>
                    )}
                    {parseResult.late_fee_amount && (
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Late Fee</span>
                        <span className="font-bold">${parseResult.late_fee_amount}</span>
                      </div>
                    )}
                    {parseResult.tenant_name && (
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Tenant</span>
                        <span className="font-bold">{parseResult.tenant_name}</span>
                      </div>
                    )}
                    {parseResult.property_address && (
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Property</span>
                        <span className="font-bold">{parseResult.property_address}</span>
                      </div>
                    )}
                    {parseResult.important_clauses && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-xs text-amber-700 uppercase mb-2">Important Clauses</p>
                        <p className="text-sm">{parseResult.important_clauses}</p>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(parseResult, null, 2));
                        toast.success("Copied to clipboard!");
                      }}
                    >
                      Copy Extracted Data
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="flex justify-between items-center mb-6">
            <p className="text-slate-500">Store lease documents, tenant IDs, and other files</p>
            <Button onClick={() => setUploadDialogOpen(true)} className="bg-green-700 hover:bg-green-800">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>

          {documents.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No documents uploaded yet</p>
                <Button variant="outline" className="mt-4" onClick={() => setUploadDialogOpen(true)}>
                  Upload First Document
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        {getDocIcon(doc.document_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{doc.filename}</p>
                        <p className="text-xs text-slate-500">
                          {doc.document_type.replace("_", " ")} • {(doc.file_size / 1024).toFixed(1)} KB
                        </p>
                        {doc.notes && (
                          <p className="text-xs text-slate-400 mt-1 truncate">{doc.notes}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lease">Lease Agreement</SelectItem>
                  <SelectItem value="tenant_id">Tenant ID</SelectItem>
                  <SelectItem value="addendum">Lease Addendum</SelectItem>
                  <SelectItem value="renewal">Renewal Agreement</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>File</Label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Add any notes about this document..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadDocument} disabled={uploading || !uploadFile}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Lease Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLease ? "Edit Lease" : "Create Lease"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tenant *</Label>
                <Select
                  value={formData.tenant_id}
                  onValueChange={(v) => setFormData({...formData, tenant_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Property *</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={(v) => setFormData({...formData, property_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Rent *</Label>
                <Input
                  type="number"
                  value={formData.monthly_rent}
                  onChange={(e) => setFormData({...formData, monthly_rent: e.target.value})}
                  placeholder="1500"
                />
              </div>
              <div className="space-y-2">
                <Label>Security Deposit</Label>
                <Input
                  type="number"
                  value={formData.security_deposit}
                  onChange={(e) => setFormData({...formData, security_deposit: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Late Fee ($)</Label>
                <Input
                  type="number"
                  value={formData.late_fee_amount}
                  onChange={(e) => setFormData({...formData, late_fee_amount: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Grace Days</Label>
                <Input
                  type="number"
                  value={formData.late_fee_grace_days}
                  onChange={(e) => setFormData({...formData, late_fee_grace_days: e.target.value})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingLease ? "Update" : "Create"} Lease
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteLease} onOpenChange={() => setDeleteLease(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lease?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the lease record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeaseHub;
